import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Award, CheckCircle } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.echt.gold';

const Diamantenankauf = () => {
  return (
    <>
      <Helmet>
        <title>Diamantenankauf - Echt Gold | Zertifizierte Diamanten</title>
        <meta name="description" content="Diamantenankauf mit professioneller Bewertung. Lose Diamanten und Diamantschmuck. Fair nach den 4C bewertet. Jetzt Diamanten verkaufen!" />
        <link rel="canonical" href={`${BASE_URL}/diamantenankauf`} />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": `${BASE_URL}/`},
            {"@type": "ListItem", "position": 2, "name": "Diamantenankauf", "item": `${BASE_URL}/diamantenankauf`}
          ]
        })}
      </script>

      <div className="bg-black/90 py-8 border-b border-gold/20">
        <div className="container mx-auto px-4">
          <div className="text-sm text-gray-400 mb-2">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-gold">Diamantenankauf</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gold-text">Diamantenankauf</h1>
        </div>
      </div>

      <section className="section section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 gold-text text-center">Diamanten professionell verkaufen</h2>
            <p className="text-gray-300 mb-8 text-lg text-center">
              Wir kaufen lose Diamanten und Diamantschmuck. Unsere Experten bewerten Ihre Steine nach den 4C (Carat, Cut, Color, Clarity) und internationalen Standards.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="card">
                <h3 className="text-xl font-bold mb-4 gold-text">Was wir ankaufen</h3>
                <ul className="text-gray-300 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span>Lose zertifizierte Diamanten</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span>Diamantringe & Verlobungsringe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span>Diamantketten & Colliers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span>Diamantohrringe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span>Diamantarmbänder</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={18} />
                    <span>Brillanten jeder Größe</span>
                  </li>
                </ul>
              </div>
              <div className="card">
                <h3 className="text-xl font-bold mb-4 gold-text">Bewertungskriterien (4C)</h3>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-bold text-gold mb-1">Carat (Gewicht)</h4>
                    <p className="text-gray-300 text-sm">Das Gewicht in Karat bestimmt maßgeblich den Wert</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gold mb-1">Cut (Schliff)</h4>
                    <p className="text-gray-300 text-sm">Qualität des Schliffs beeinflusst das Funkeln</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gold mb-1">Color (Farbe)</h4>
                    <p className="text-gray-300 text-sm">Von farblos (D) bis deutlich gefärbt (Z)</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gold mb-1">Clarity (Reinheit)</h4>
                    <p className="text-gray-300 text-sm">Grad der Einschlüsse und Unreinheiten</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card bg-gold/10 border-gold/30">
              <div className="flex gap-4 items-start">
                <Award className="text-gold flex-shrink-0" size={32} />
                <div>
                  <h3 className="text-xl font-bold mb-2 gold-text">Zertifikate erhöhen den Wert</h3>
                  <p className="text-gray-300">
                    Besitzen Sie Zertifikate von GIA, HRD oder IGI? Diese erhöhen den Wert und erleichtern die Bewertung. Aber auch ohne Zertifikat kaufen wir Ihre Diamanten an - unsere Experten führen eine präzise Bewertung durch.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="card text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 gold-text">Diamanten verkaufen?</h2>
            <p className="text-gray-300 mb-8">
              Vereinbaren Sie einen Termin für die professionelle Bewertung Ihrer Diamanten
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary">Jetzt Termin vereinbaren</Link>
              <a href="tel:023139085116" className="btn-secondary">0231 39085116</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Diamantenankauf;